import { ColorMode } from '@color';
import { Axis, Point, Rect, Size, size } from '@geom';
import { IString, type LocaleId } from '@strings';
import { BasicTextStyle, layoutText } from '@typography';
import { assertInstanceOf, expectDefined, hasAnyKeys, immerable } from '@utils';
import { VisualCache } from '@visual';
import { ElementDecl } from './ElementDecl';
import { LayoutElement } from './LayoutElement';
import { SnorfLayout } from './SnorfLayout';
import type {
  ChildAlignment,
  ElementId,
  LayoutConfig,
  Padding,
  SizingMinMax
} from './types';
import {
  axisPadding,
  LayoutAlignmentX,
  LayoutAlignmentY,
  LayoutDirection,
  SizingType
} from './types';

interface ChildPlan {
  type: SizingType;
  index: number;
  min?: number;
  max?: number;
  percent?: number;
  fixed?: number;
}

export class SnorfSpec {
  [immerable] = true;

  private _elementDecls = new Map<ElementId, ElementDecl>();

  getElement(id: string | ElementId): ElementDecl {
    const elementDecl = this._elementDecls.get(id as ElementId);
    if (!elementDecl) {
      throw new Error(`Element declaration with id ${id} not found`);
    }
    return elementDecl;
  }

  get elementsCount(): number {
    return this._elementDecls.size;
  }

  get allElements(): ElementDecl[] {
    return Array.from(this._elementDecls.values());
  }

  addElement(e: ElementDecl): this {
    if (hasAnyKeys(this._elementDecls, [e.id])) {
      throw new Error(`Element declaration with id ${e.id} already exists`);
    }
    if (e.textData !== undefined) {
      assertInstanceOf(e.textData.text, IString);
    }
    this._elementDecls.set(e.id, e);
    return this;
  }

  hasElement(id: string | ElementId): boolean {
    return this._elementDecls.has(id as ElementId);
  }

  performLayout(
    layoutDimensions: Size,
    colorMode: ColorMode,
    locale: LocaleId,
    cache: VisualCache,
    opts?: { mark?: string }
  ): SnorfLayout {
    const mark =
      opts?.mark !== undefined
        ? `Snorf:performLayout:${opts.mark}`
        : 'Snorf:performLayout';
    performance.mark(`${mark}:start`);
    const layout = new SnorfLayout();
    const rootLE = new LayoutElement();
    rootLE.id = SnorfLayout.ROOT_ID;
    rootLE.dimensions = layoutDimensions;
    layout.addLayoutElement(rootLE);
    const currentRoots = Array.from(this._elementDecls.values()).filter(d => d.isRoot);

    const addChildren = (
      spec: SnorfSpec,
      parent: LayoutElement,
      children: ElementDecl[]
    ) => {
      children.forEach(child => {
        const childLE = new LayoutElement();

        childLE.id = child.id;
        childLE.parentId = parent.id;
        childLE.layoutConfig = child.layoutConfig; // Immutable with immer
        childLE.backgroundColor = child.backgroundColor;
        childLE.cornerRadius = { ...child.cornerRadius };
        childLE.border = child.border; // Immutable with immer
        childLE.textData = child.textData ? { ...child.textData } : undefined;
        childLE.imageData = child.imageData ? { ...child.imageData } : undefined;
        childLE.hovered = child.hovered;
        childLE.selectionHaloOpacity = child.isSelected ? 1 : 0;
        childLE.copyOverlayOpacity = child.hasCopyOverlay ? 1 : 0;
        childLE.opacity = child.opacity;
        childLE.clip = child.clip;
        childLE.zIndex = child.zIndex;

        layout.addLayoutElement(childLE);
        parent.addChild(childLE.id);
        addChildren(spec, childLE, spec.getChildren(child));
      });
    };

    addChildren(this, rootLE, currentRoots);

    measureTextElements(layout, colorMode, locale, cache);
    measureImageElements(layout, cache);

    sizeContainersAlongAxis(layout, Axis.X);
    sizeContainersAlongAxis(layout, Axis.Y);
    assignPositions(layout);

    // Resize the root element to fit its actual content
    resizeRootToContent(layout);

    // Ensure the root bounds are integral
    const rootElement = layout.getLayoutElement(SnorfLayout.ROOT_ID);
    const integralBounds = rootElement.bounds.integral;
    rootElement.dimensions = integralBounds.size;

    performance.mark(`${mark}:end`);
    performance.measure(mark, `${mark}:start`, `${mark}:end`);
    return layout;
  }

  getChildren(element: ElementDecl): ElementDecl[] {
    return element.childIds.map(id => this.getElement(id));
  }
}

function sizeContainersAlongAxis(layout: SnorfLayout, axis: Axis): void {
  const layoutElement = layout.getLayoutElement(SnorfLayout.ROOT_ID);
  const parentSize = layoutElement.dimensions.majorAxis(axis);
  sizeDfs(layout, layoutElement, parentSize, axis);
}

function sizeDfs(
  layout: SnorfLayout,
  elem: LayoutElement,
  parentSize: number | undefined,
  axis: Axis
): void {
  const rec = layout.getLayoutElement(elem.id);
  const layoutConfig = elem.layoutConfig;
  const sizingAxis = layoutConfig.sizing.atAxis(axis);
  const children = layout.getChildren(elem.id);
  const padding = axisPadding(axis, layoutConfig.padding);
  const childGap = layoutConfig.childGap;

  // Text elements are treated as leaf nodes with fixed dimensions from measurement
  if (elem.isText && rec.textData?.measured) {
    // For text elements, use the measured dimensions and don't allow sizing overrides
    rec.dimensions = rec.textData.measured.size;
    // Early return - text elements have no children and don't participate in normal sizing
    return;
  }

  // Image elements are treated as leaf nodes and respect layout constraints
  if (elem.isImage && rec.imageData?.source.isLoaded) {
    const intrinsicSize = rec.imageData.source.intrinsicSize;
    const layoutX = elem.layoutConfig.sizing.atAxis(Axis.X);
    const layoutY = elem.layoutConfig.sizing.atAxis(Axis.Y);

    // Handle aspect ratio calculations for images with mixed constraints
    if (layoutX.type === SizingType.FIT && layoutY.type === SizingType.FIXED) {
      // X is FIT, Y is constrained - calculate width to preserve aspect ratio
      const aspectRatio = intrinsicSize.width / intrinsicSize.height;
      const height = typeof layoutY.size === 'number' ? layoutY.size : layoutY.size.min;
      rec.dimensions = size(height * aspectRatio, height);
      return;
    } else if (layoutX.type === SizingType.FIXED && layoutY.type === SizingType.FIT) {
      // Y is FIT, X is constrained - calculate height to preserve aspect ratio
      const aspectRatio = intrinsicSize.width / intrinsicSize.height;
      const width = typeof layoutX.size === 'number' ? layoutX.size : layoutX.size.min;
      rec.dimensions = size(width, width / aspectRatio);
      return;
    } else if (layoutX.type === SizingType.FIT && layoutY.type === SizingType.FIT) {
      // Both axes are FIT - use intrinsic dimensions
      rec.dimensions = intrinsicSize;
      return;
    }

    // For explicitly constrained images (both X and Y), let normal sizing proceed
    // The image will be sized to fit its container, and ObjectFit will handle rendering
  }

  if (sizingAxis.type === SizingType.FIXED) {
    const fixed =
      typeof sizingAxis.size === 'number' ? sizingAxis.size : sizingAxis.size.min;
    rec.dimensions =
      axis === Axis.X
        ? size(fixed, rec.dimensions.height)
        : size(rec.dimensions.width, fixed);
  } else if (sizingAxis.type === SizingType.PERCENT && parentSize !== undefined) {
    const percent = sizingAxis.size as number;
    const contentSize = parentSize - padding;
    const thisSize = percent * contentSize;
    rec.dimensions =
      axis === Axis.X
        ? size(thisSize, rec.dimensions.height)
        : size(rec.dimensions.width, thisSize);
  } else if (
    sizingAxis.type === SizingType.GROW &&
    parentSize !== undefined &&
    rec.dimensions.majorAxis(axis) === 0
  ) {
    const s = sizingAxis.size as SizingMinMax;
    const growSize = Math.max(s.min, Math.min(s.max, parentSize - padding));
    rec.dimensions =
      axis === Axis.X
        ? size(growSize, rec.dimensions.height)
        : size(rec.dimensions.width, growSize);
  }

  const plans = collectChildPlans(children, axis);
  const mySizeInitial = rec.dimensions.majorAxis(axis);
  let mySize = mySizeInitial;
  if (mySize === 0 && parentSize !== undefined) {
    mySize = parentSize;
  }

  if (children.length > 0 && mySize > 0) {
    assignChildSizes(layout, children, plans, axis, mySize, padding, childGap);
  }

  for (const child of children) {
    const parentContentSize = mySize - padding;
    sizeDfs(layout, child, parentContentSize, axis);
  }

  if (sizingAxis.type === SizingType.FIT) {
    const contentSize =
      measureFitContentSize(layout, children, axis, layoutConfig, childGap) + padding;
    if (typeof sizingAxis.size === 'object') {
      const minMax = sizingAxis.size;
      rec.dimensions =
        axis === Axis.X
          ? size(
              Math.max(minMax.min, Math.min(minMax.max, contentSize)),
              rec.dimensions.height
            )
          : size(
              rec.dimensions.width,
              Math.max(minMax.min, Math.min(minMax.max, contentSize))
            );
    } else {
      rec.dimensions =
        axis === Axis.X
          ? size(contentSize, rec.dimensions.height)
          : size(rec.dimensions.width, contentSize);
    }
  }
}

function collectChildPlans(children: LayoutElement[], axis: Axis): ChildPlan[] {
  const plans: ChildPlan[] = [];
  let totalPercent = 0;
  for (let i = 0; i < children.length; ++i) {
    const childSizing = children[i].layoutConfig.sizing.atAxis(axis);
    switch (childSizing.type) {
      case SizingType.FIXED: {
        const fixed =
          typeof childSizing.size === 'number' ? childSizing.size : childSizing.size.min;
        plans.push({ type: SizingType.FIXED, index: i, fixed });
        break;
      }
      case SizingType.PERCENT: {
        const percent = childSizing.size as number;
        plans.push({ type: SizingType.PERCENT, index: i, percent });
        totalPercent += percent;
        break;
      }
      case SizingType.GROW: {
        plans.push({
          type: SizingType.GROW,
          index: i,
          min: (childSizing.size as SizingMinMax).min,
          max: (childSizing.size as SizingMinMax).max
        });
        break;
      }
      case SizingType.FIT: {
        plans.push({ type: SizingType.FIT, index: i });
        break;
      }
    }
  }
  if (totalPercent > 1.0 + 1e-5) {
    throw new Error('Total child percent exceeds 100% in layout.');
  }
  return plans;
}

function assignChildSizes(
  layout: SnorfLayout,
  children: LayoutElement[],
  plans: ChildPlan[],
  axis: Axis,
  mySize: number,
  padding: number,
  gap: number
): void {
  let usedContent = 0;
  const totalGap = gap * Math.max(0, children.length - 1);
  const contentSize = mySize - padding;

  for (const plan of plans) {
    if (plan.type === SizingType.FIXED) {
      const childRec = layout.getLayoutElement(children[plan.index].id);
      const fixed = expectDefined(plan.fixed);
      childRec.dimensions =
        axis === Axis.X
          ? size(fixed, childRec.dimensions.height)
          : size(childRec.dimensions.width, fixed);
      usedContent += fixed;
    }
  }

  for (const plan of plans) {
    if (plan.type === SizingType.PERCENT) {
      const childRec = layout.getLayoutElement(children[plan.index].id);
      const childSize = expectDefined(plan.percent) * contentSize;
      childRec.dimensions =
        axis === Axis.X
          ? size(childSize, childRec.dimensions.height)
          : size(childRec.dimensions.width, childSize);
      usedContent += childSize;
    }
  }

  const growPlans = plans.filter(p => p.type === SizingType.GROW);
  const growSpace = contentSize - totalGap - usedContent;
  if (growPlans.length > 0 && growSpace > 0) {
    const growSize = growSpace / growPlans.length;
    for (const plan of growPlans) {
      const childRec = layout.getLayoutElement(children[plan.index].id);
      childRec.dimensions =
        axis === Axis.X
          ? size(growSize, childRec.dimensions.height)
          : size(childRec.dimensions.width, growSize);
    }
  }
}

function measureFitContentSize(
  layout: SnorfLayout,
  children: LayoutElement[],
  axis: Axis,
  layoutConfig: LayoutConfig,
  childGap: number
): number {
  let contentSizeOnAxis = 0;
  if (children.length > 0) {
    const isMainAxis =
      (axis === Axis.X &&
        (layoutConfig.direction === LayoutDirection.LEFT_TO_RIGHT ||
          layoutConfig.direction === LayoutDirection.RIGHT_TO_LEFT)) ||
      (axis === Axis.Y && layoutConfig.direction === LayoutDirection.TOP_TO_BOTTOM);
    if (isMainAxis) {
      for (const child of children) {
        const childRec = layout.getLayoutElement(child.id);
        contentSizeOnAxis += childRec.dimensions.majorAxis(axis);
      }
      contentSizeOnAxis += childGap * Math.max(0, children.length - 1);
    } else {
      for (const child of children) {
        const childRec = layout.getLayoutElement(child.id);
        contentSizeOnAxis = Math.max(
          contentSizeOnAxis,
          childRec.dimensions.majorAxis(axis)
        );
      }
    }
  }
  return contentSizeOnAxis;
}

function assignPositions(layout: SnorfLayout): void {
  assignPositionsRecursive(
    layout,
    layout.getLayoutElement(SnorfLayout.ROOT_ID),
    Point.ZERO
  );
}

function assignPositionsRecursive(
  layout: SnorfLayout,
  layoutElement: LayoutElement,
  parentOrigin: Point
): void {
  layoutElement.origin = parentOrigin;

  const children = layout.getChildren(layoutElement.id);
  if (children.length === 0) return;

  const layoutConfig = layoutElement.layoutConfig;
  const direction = layoutConfig.direction;
  const gap = layoutConfig.childGap;
  const pad = layoutConfig.padding;
  const alignment = layoutConfig.childAlignment;

  let cursor: Point;
  switch (direction) {
    case LayoutDirection.LEFT_TO_RIGHT:
    default:
      cursor = new Point(parentOrigin.x + pad.leading, parentOrigin.y + pad.top);
      break;
    case LayoutDirection.RIGHT_TO_LEFT:
      cursor = new Point(
        parentOrigin.x + layoutElement.dimensions.width - pad.leading,
        parentOrigin.y + pad.top
      );
      break;
    case LayoutDirection.TOP_TO_BOTTOM:
      cursor = new Point(parentOrigin.x + pad.leading, parentOrigin.y + pad.top);
      break;
  }

  for (const childDecl of children) {
    const childElt = expectDefined(layout.getLayoutElement(childDecl.id));
    const childSize = childElt.dimensions;
    let childOrigin: Point;

    const alignOffset = alignmentOffset(
      layout,
      direction,
      alignment,
      pad,
      layoutElement,
      childSize
    );

    switch (direction) {
      case LayoutDirection.LEFT_TO_RIGHT:
      default:
        childOrigin = new Point(cursor.x, cursor.y + alignOffset);
        assignPositionsRecursive(layout, childDecl, childOrigin);
        cursor = cursor.add(new Point(childSize.width + gap, 0));
        break;
      case LayoutDirection.RIGHT_TO_LEFT:
        childOrigin = new Point(cursor.x - childSize.width, cursor.y + alignOffset);
        assignPositionsRecursive(layout, childDecl, childOrigin);
        cursor = cursor.add(new Point(-childSize.width - gap, 0));
        break;
      case LayoutDirection.TOP_TO_BOTTOM:
        childOrigin = new Point(cursor.x + alignOffset, cursor.y);
        assignPositionsRecursive(layout, childDecl, childOrigin);
        cursor = cursor.add(new Point(0, childSize.height + gap));
        break;
    }
  }
}

function alignmentOffset(
  layout: SnorfLayout,
  direction: LayoutDirection,
  alignment: ChildAlignment,
  pad: Padding,
  parent: LayoutElement,
  childSize: Size
): number {
  switch (direction) {
    case LayoutDirection.LEFT_TO_RIGHT:
    case LayoutDirection.RIGHT_TO_LEFT:
      switch (alignment.y) {
        case LayoutAlignmentY.CENTER:
          return (parent.dimensions.height - pad.top - pad.bottom - childSize.height) / 2;
        case LayoutAlignmentY.BOTTOM:
          return parent.dimensions.height - pad.top - pad.bottom - childSize.height;
        default:
          return 0;
      }
    case LayoutDirection.TOP_TO_BOTTOM:
      switch (alignment.x) {
        case LayoutAlignmentX.CENTER:
          return (
            (parent.dimensions.width - pad.leading - pad.trailing - childSize.width) / 2
          );
        case LayoutAlignmentX.RIGHT:
          return parent.dimensions.width - pad.leading - pad.trailing - childSize.width;
        default:
          return 0;
      }
  }
}

function measureTextElements(
  layout: SnorfLayout,
  colorMode: ColorMode,
  locale: LocaleId,
  cache: VisualCache
): void {
  for (const decl of layout.allElements) {
    const textDecl = decl.textData;
    if (!textDecl) continue;
    const layoutElement = layout.getLayoutElement(decl.id);
    const style = textDecl.layoutOptions.basicTextStyle;
    const measured = layoutText(
      textDecl.text.get(locale),
      textDecl.color,
      colorMode,
      textDecl.layoutOptions,
      cache.paragraphCache,
      style ?? BasicTextStyle.PLAIN
    );
    layoutElement.textData = {
      text: textDecl.text,
      color: textDecl.color,
      layoutOptions: { ...textDecl.layoutOptions },
      measured
    };
    layoutElement.dimensions = measured.size;
  }
}

function measureImageElements(layout: SnorfLayout, cache: VisualCache): void {
  for (const decl of layout.allElements) {
    const imageDecl = decl.imageData;
    if (!imageDecl?.source.isLoaded) continue;

    const layoutElement = layout.getLayoutElement(decl.id);
    cache.imageCache.set(imageDecl.source);
    const intrinsicSize = imageDecl.source.intrinsicSize;

    // Initialize with intrinsic size - actual sizing will be handled in #sizeDfs
    layoutElement.dimensions = intrinsicSize;
  }
}

function resizeRootToContent(layout: SnorfLayout): void {
  const rootLE = layout.getLayoutElement(SnorfLayout.ROOT_ID);
  const children = layout.getChildren(rootLE.id);

  if (children.length === 0) {
    // No content, set minimal size
    rootLE.dimensions = size(1, 1);
    return;
  }

  // Calculate the union of all top-level content elements
  let contentBounds: Rect | undefined;

  for (const child of children) {
    contentBounds = child.bounds.union(contentBounds);
  }

  // Resize root to fit the content bounds
  if (contentBounds) {
    rootLE.dimensions = contentBounds.size;
  }
}
